import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
  Grid2 as Grid,
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { MainLayoutContainer } from "components/Layout";
import { DomainColorSquares } from "components/Requirements";
import { TagManager } from "components/TagManager";
import { Tag } from "types/tag";
import { EntitySelector, EntitySelectorWithTabs } from "modals/EntitySelector";
import { getFilteredList } from "utils/Array";
import {
  useAddAcademicYearMutation,
  useAddAcademicYearRequirementMutation,
  useAddAcademicYearTagMutation,
  useGetAcademicYearsTemplateQuery,
  useGetOrgRequirementsByOrgLazyQuery,
} from "graphql/__generated__/hooks";
import { ErrorMessage, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { AcademicYear } from "types/academicYear";
import { getAcademicYearDates } from "utils/Datetime";
import { Requirement } from "types/requirement";
import { BREADCRUMB_PAGE } from "components/Breadcrumb/constants";
import { Messages, NotificationSeverity, REQUIREMENT } from "helpers/constants";
import { useNotifications } from "hooks/useNotifications";
import { useProgramOrganization } from "hooks/useProgramOrganization";
import { useClinicText } from "hooks/useClinicText";

export const AcademicYearCreateByOrgProgram: FC = () => {
  const { organizationId } = useParams();
  const { program } = useProgramOrganization();
  const { textYearlyCurriculum } = useClinicText();
  const navigate = useNavigate();
  const [tags, setTags] = useState<Tag[]>([]);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [openEntitySelector, setOpenEntitySelector] = useState(false);
  const [openSelectTemplates, setOpenSelectTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AcademicYear | null>(
    null,
  );
  const [loading, setLoading] = useState(false);

  const [addAcademicYear] = useAddAcademicYearMutation();
  const [addAcademicYearTag] = useAddAcademicYearTagMutation();
  const { data: templates } = useGetAcademicYearsTemplateQuery();
  const [getOrgRequirementsByOrg, { data: orgData }] =
    useGetOrgRequirementsByOrgLazyQuery();

  const backRoute = useMemo(() => {
    return `/organization/${organizationId}/yearly-curriculum/`;
  }, [organizationId]);

  const reqList = useMemo<Requirement[]>(() => {
    return (
      orgData?.organization_requirement?.map(
        (req) => req.requirement as Requirement,
      ) || []
    );
  }, [orgData]);

  const { start_date, end_date } = getAcademicYearDates();
  const { notify } = useNotifications();

  const [addAcademicYearRequirement] = useAddAcademicYearRequirementMutation();

  const initialValues = {
    name: "",
    start_date: start_date,
    end_date: end_date,
  };

  const validationSchema = () =>
    Yup.object().shape({
      name: Yup.string().required("This field is required"),
      start_date: Yup.string().required("This field is required"),
      end_date: Yup.string().required("This field is required"),
    });

  const handleRequirements = async (academicYearId: string) => {
    const data = requirements.map((req) => ({
      academic_year_id: academicYearId,
      requirement_id: req.requirement_id,
    }));
    await addAcademicYearRequirement({ variables: { objects: data } });
  };

  const handleAcademicYearAndTags = async (academicYearId: string) => {
    if (tags.length === 0) return;
    tags.forEach((tag) => {
      addAcademicYearTag({
        variables: {
          academicYearId: academicYearId,
          tagId: tag.tag_id,
        },
      });
    });
  };

  /* Create program */
  const handleCreateAcademicYear = async (reqValues: AcademicYear) => {
    setLoading(true);
    const result = await addAcademicYear({
      variables: { ...reqValues, program_id: program?.program_id },
    });

    const academicYearId =
      result.data?.insert_academic_year?.returning &&
      result.data?.insert_academic_year?.returning[0]?.academic_year_id;
    if (academicYearId) {
      await handleAcademicYearAndTags(academicYearId).then();
      await handleRequirements(academicYearId).then();
    }
    setLoading(false);
    navigate(backRoute);
    notify({
      message: Messages.successAcademicYearCreated,
      severity: NotificationSeverity.success,
    });
  };

  useEffect(() => {
    if (organizationId) {
      getOrgRequirementsByOrg({
        variables: {
          orgId: organizationId,
        },
      });
    }
  }, [organizationId]);

  useEffect(() => {
    if (selectedTemplate) {
      setTags(selectedTemplate.tags || []);
      setRequirements(selectedTemplate.requirements || []);

      formik.setValues({
        name: selectedTemplate.name,
        start_date,
        end_date,
      });
    }
  }, [selectedTemplate]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const reqValues = {
        name: values.name,
        start_date: values.start_date,
        end_date: values.end_date,
      };
      handleCreateAcademicYear(reqValues as AcademicYear).then();
    },
  });

  const handleAddTag = async (tag: Tag) => {
    setTags([...tags, tag]);
  };

  const handleDeleteTag = async (tagToRemove: Tag) => {
    const newTags = tags.filter((tag) => tag.tag_id !== tagToRemove.tag_id);
    setTags(newTags);
  };

  return (
    <MainLayoutContainer
      title={`Create ${textYearlyCurriculum}`}
      buttonActions={
        <Button
          variant="outlined"
          color="primary"
          onClick={() => {
            setOpenSelectTemplates(true);
          }}
        >
          Select Program Template
        </Button>
      }
      breadcrumb={
        organizationId
          ? BREADCRUMB_PAGE.YEARLY_CURRICULUM
          : BREADCRUMB_PAGE.ACADEMIC_YEAR
      }
    >
      <Box sx={{ maxWidth: 860 }}>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <Box mb={2}>
              <TextField
                variant="standard"
                onChange={formik.handleChange}
                fullWidth
                name="name"
                label="Name"
                type="text"
                id="name"
                value={formik?.values?.name}
              />
              <ErrorMessage name="name" />
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ md: 6, xs: 12 }}>
                <Box mb={2}>
                  <TextField
                    variant="standard"
                    onChange={formik.handleChange}
                    fullWidth
                    name="start_date"
                    label="Start Date"
                    type="date"
                    id="start_date"
                    value={formik?.values?.start_date}
                  />
                  <ErrorMessage name="start_date" />
                </Box>
              </Grid>
              <Grid size={{ md: 6, xs: 12 }}>
                <Box mb={2}>
                  <TextField
                    variant="standard"
                    onChange={formik.handleChange}
                    fullWidth
                    name="end_date"
                    label="End Date"
                    type="date"
                    id="end_date"
                    value={formik?.values?.end_date}
                  />
                  <ErrorMessage name="end_date" />
                </Box>
              </Grid>
            </Grid>

            <TagManager
              tags={tags}
              onAdd={handleAddTag}
              onDelete={handleDeleteTag}
              isEditing={true}
            />

            <Box mt={2}>
              <Typography variant="h6">Requirements</Typography>

              <Stack spacing={2}>
                {requirements.length === 0 && (
                  <Typography variant="body2" align="center">
                    No requirement, add more requirement.
                  </Typography>
                )}
                {requirements.length > 0 && (
                  <List>
                    {requirements.map((requirement, i) => (
                      <ListItem
                        key={requirement.requirement_id}
                        sx={{
                          borderBottom:
                            i === requirements.length - 1
                              ? "none"
                              : "1px solid rgba(0,0,0,0.2)",
                        }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="delete"
                            onClick={() => {
                              setRequirements(
                                requirements.filter(
                                  (data) =>
                                    data.requirement_id !==
                                    requirement.requirement_id,
                                ),
                              );
                            }}
                          >
                            <Delete />
                          </IconButton>
                        }
                      >
                        <ListItemText
                          primary={requirement.name}
                          secondary={
                            <Stack spacing={0.5} alignItems="flex-start">
                              {requirement.description?.slice(0, 120) ? (
                                <span>
                                  {requirement.description.slice(0, 120)}
                                </span>
                              ) : null}
                              <DomainColorSquares requirement={requirement} />
                            </Stack>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}

                <Box></Box>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setOpenEntitySelector(true);
                  }}
                  startIcon={<Add />}
                >
                  Add Requirement
                </Button>
              </Stack>
            </Box>

            <EntitySelectorWithTabs
              open={openEntitySelector}
              onClose={() => setOpenEntitySelector(false)}
              title={"Select Requirements"}
              listEntities={getFilteredList(
                reqList,
                requirements,
                "requirement_id",
              )}
              filterCriteria={(search: string, data: any) =>
                data.name.toLowerCase().includes(search.toLowerCase())
              }
              onSelect={(selected: any) => {
                const toAdd = Array.isArray(selected) ? selected : [selected];
                setRequirements([...requirements, ...toAdd]);
              }}
              displayField="name"
              keyField="requirement_id"
              multiSelect
              tabs={[
                {
                  label: "EPA",
                  filterTabCriteria: (requirement: any) =>
                    requirement?.type === REQUIREMENT.REQUIREMENT_EPA,
                },
                {
                  label: "Competency",
                  filterTabCriteria: (requirement: any) =>
                    requirement?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
                },
              ]}
            />

            <Box mt={2}>
              <Stack direction="row-reverse" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={loading}
                >
                  Save Changes
                </Button>
                <Button variant="outlined" onClick={() => navigate(backRoute)}>
                  Cancel
                </Button>
              </Stack>
            </Box>
          </form>
        </FormikProvider>

        {openSelectTemplates && (
          <EntitySelector
            open={openSelectTemplates}
            onClose={() => setOpenSelectTemplates(false)}
            title={"Select Program Template"}
            listEntities={templates?.academic_year || []}
            filterCriteria={(search: string, data: any) =>
              data.name.toLowerCase().includes(search.toLowerCase())
            }
            onSelect={(selected: any) => {
              setSelectedTemplate({
                ...selected,
                tags: selected.tags?.map((tag: any) => tag.tag),
                requirements: selected.requirements?.map(
                  (req: any) => req.requirement as Requirement,
                ),
              });
              setOpenSelectTemplates(false);
            }}
            displayField="name"
            keyField="academic_year_id"
          />
        )}
      </Box>
    </MainLayoutContainer>
  );
};
